import {
  Dispatch, JSX, SetStateAction, useState,
} from 'react';
import { IBiomaterial, IBiomaterialFilters } from '@zero-dash/types';
import ListMenu from '@/components/SearchFilterBar/ListMenu';
import { FilterOption } from '@/types/Search.types';
import FilterButton from '@/components/SearchFilterBar/Buttons/FilterButton';

interface IProps {
  biomaterials: IBiomaterial[];
  toggled: IBiomaterialFilters;
  setToggled: Dispatch<SetStateAction<IBiomaterialFilters>>;
  emptyOptions: IBiomaterialFilters;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setSearchValue: Dispatch<SetStateAction<string>>;
}

export default function BiomaterialsFilterButton({
  biomaterials,
  toggled,
  setToggled,
  emptyOptions,
  loading,
  setLoading,
  setSearchValue,
}: IProps): JSX.Element {
  const [anchorElSpecimenPurpose, setAnchorElSpecimenPurpose] = useState<null | HTMLElement>(null);
  const [anchorElSubtype, setAnchorElSubtype] = useState<null | HTMLElement>(null);
  const [anchorElInventoryStatus, setAnchorElInventoryStatus] = useState<null | HTMLElement>(null);
  const [anchorElContainerType, setAnchorElContainerType] = useState<null | HTMLElement>(null);

  const getChipLabel = (list: string[], length = 4): string => (
    list.length > length ? (
      `${list
        ?.slice(0, length)
        .map((g) => g.toUpperCase().split('::')[0])
        .join('; ')} + ${
        list.length - length
      } more`
    ) : (
      list
        ?.map((g) => g.toUpperCase().split('::')[0])
        .join('; ')
    )
  );

  const lmBiomaterialsFilterOptions: FilterOption[] = [
    {
      label: 'Specimen Purpose',
      value: 'specimenPurpose',
      type: 'menu',
      check: Boolean(toggled.specimenPurpose?.length),
      defaultVal: [],
      submenu: (
        <ListMenu
          anchorEl={anchorElSpecimenPurpose}
          setAnchorEl={setAnchorElSpecimenPurpose}
          value={toggled.specimenPurpose || []}
          onChange={(newValue): void => setToggled((prev) => (prev
            ? { ...prev, specimenPurpose: newValue }
            : prev
          ))}
          menuOptions={[...new Set(biomaterials.map((b) => b.specimenPurpose || 'Not set'))]}
          loading={loading}
          setLoading={setLoading}
          isVariant={false}
        />
      ),
      setAnchor: (target) => setAnchorElSpecimenPurpose(target),
      chipLabel: () => getChipLabel(toggled.specimenPurpose || [], 1),
    },
    {
      label: 'Subtype',
      value: 'subtype',
      type: 'menu',
      check: Boolean(toggled.subtype?.length),
      defaultVal: [],
      submenu: (
        <ListMenu
          anchorEl={anchorElSubtype}
          setAnchorEl={setAnchorElSubtype}
          value={toggled.subtype || []}
          onChange={(newValue): void => setToggled((prev) => (prev
            ? { ...prev, subtype: newValue }
            : prev
          ))}
          menuOptions={[...new Set(biomaterials.map((b) => b.subtype || 'Not set'))]}
          loading={loading}
          setLoading={setLoading}
          isVariant={false}
        />
      ),
      setAnchor: (target) => setAnchorElSubtype(target),
      chipLabel: () => getChipLabel(toggled.subtype || [], 1),
    },
    {
      label: 'Inventory Status',
      value: 'inventoryStatus',
      type: 'menu',
      check: Boolean(toggled.inventoryStatus?.length),
      defaultVal: [],
      submenu: (
        <ListMenu
          anchorEl={anchorElInventoryStatus}
          setAnchorEl={setAnchorElInventoryStatus}
          value={toggled.inventoryStatus || []}
          onChange={(newValue): void => setToggled((prev) => (prev
            ? { ...prev, inventoryStatus: newValue }
            : prev
          ))}
          menuOptions={[...new Set(biomaterials.map((b) => b.inventoryStatus || 'Not set'))]}
          loading={loading}
          setLoading={setLoading}
          isVariant={false}
        />
      ),
      setAnchor: (target) => setAnchorElInventoryStatus(target),
      chipLabel: () => getChipLabel(toggled.inventoryStatus || [], 1),
    },
    {
      label: 'Container Types',
      value: 'containerType',
      type: 'menu',
      check: Boolean(toggled.containerType?.length),
      defaultVal: [],
      submenu: (
        <ListMenu
          anchorEl={anchorElContainerType}
          setAnchorEl={setAnchorElContainerType}
          value={toggled.containerType || []}
          onChange={(newValue): void => setToggled((prev) => (prev
            ? { ...prev, containerType: newValue }
            : prev
          ))}
          menuOptions={[...new Set(biomaterials.map((b) => b.containerType || 'Not set'))]}
          loading={loading}
          setLoading={setLoading}
          isVariant={false}
        />
      ),
      setAnchor: (target) => setAnchorElContainerType(target),
      chipLabel: () => getChipLabel(toggled.containerType || [], 1),
    },
    {
      label: 'Panel builder available',
      value: 'hasFiles',
      type: 'item',
      check: Boolean(toggled.hasFiles),
      defaultVal: false,
    },
  ];

  return (
    <FilterButton
      toggled={toggled}
      setToggled={setToggled}
      loading={loading}
      filterOptions={lmBiomaterialsFilterOptions}
      clearFilters={() => {
        setToggled(emptyOptions);
        setSearchValue('');
      }}
    />
  );
}
